import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowRight, CalendarDays, Check, MessageCircle, Phone, Ticket, X } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";
import RetourApp from "../components/RetourApp";

/* Mon bénévolat (Jen, 01.11.2026 : « et s'il veut changer, peut-il aussi le faire via un
   lien ») : le lien personnel de l'e-mail de confirmation d'un bénévole, comme /mon-essai et
   /mon-cours-prive. Ses créneaux ; « Changer » (une autre date ou un autre rôle, là où il
   reste de la place) ou « Annuler » (un mot pour l'école), jusqu'à 48 h avant la
   représentation. Le billet offert suit la date ; l'école est prévenue, la console suit. */

const API = `${process.env.REACT_APP_BACKEND_URL}/api/billetterie`;
const ROSE = "#DB2777";
const EMOJI = { tombola: "🎟️", patisserie: "🧁", entrees: "🎫", surveillance: "🧒", maquillage: "💄", parking: "🚗" };
const majuscule = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : "");
const sansAnnee = (t) => String(t || "").replace(/ 20\d\d/, "");
const places = (n) => (n > 0 ? `${n} place${n > 1 ? "s" : ""}` : "complet");

// La réponse du serveur, ou son message d'erreur (« Ce lien n'est pas valable… »).
async function lire(r) {
  let d = {};
  try { d = await r.json(); } catch { d = {}; }
  if (!r.ok) throw new Error(typeof d.detail === "string" && d.detail ? d.detail : `Erreur ${r.status}`);
  return d;
}

const Telephone = () => <a href="tel:+41799504031" className="font-semibold underline whitespace-nowrap">079 950 40 31</a>;

function Creneau({ c, p, jeton, onMaj }) {
  const [mode, setMode] = useState("");
  const [rep, setRep] = useState(c.representation_id);
  const [role, setRole] = useState("");
  const [motif, setMotif] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState("");
  const [fait, setFait] = useState("");
  const annule = c.statut === "annulee";
  // Les dates possibles : ouvertes, à plus de 48 h, et pas une date où il aide déjà.
  const prises = p.creneaux.filter((x) => x.id !== c.id && x.statut !== "annulee").map((x) => x.representation_id);
  const dates = p.representations.filter((r) => r.modifiable && !prises.includes(r.id));
  const choisie = p.representations.find((r) => r.id === rep);
  const actuel = (x) => rep === c.representation_id && x.role === c.role;
  const pret = choisie && role && !(rep === c.representation_id && role === c.role);
  const ouvrir = (m) => { setMode(m); setErreur(""); setFait(""); setRep(c.representation_id); setRole(""); };

  const agir = async (action, corps) => {
    setEnvoi(true); setErreur("");
    try {
      const d = await lire(await fetch(`${API}/benevolat/${encodeURIComponent(c.id)}/${action}`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ j: jeton, ...corps }),
      }));
      onMaj(d);
      setFait(d.message || "C'est noté.");
      setMode("");
    } catch (e) { setErreur(e.message); } finally { setEnvoi(false); }
  };

  return (
    <div className="rounded-3xl p-5 sm:p-6 mt-5 bg-white" data-testid={`mon-benevolat-${c.id}`}
      style={{ border: "1.5px solid rgba(15,27,45,0.10)", opacity: annule ? 0.75 : 1 }}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-bold tracking-widest uppercase" style={{ color: ROSE }}>
            {EMOJI[c.role]} {c.libelle}
          </div>
          <div className="font-display text-2xl tracking-tight mt-1">{c.titre}</div>
        </div>
        {annule && <span className="shrink-0 text-xs font-semibold rounded-full px-3 py-1" style={{ background: "#FEE2E2", color: "#B91C1C" }}
          data-testid="mon-benevolat-annule">Annulé</span>}
      </div>
      <div className="mt-3 space-y-1.5 text-sm" style={{ textDecoration: annule ? "line-through" : "none" }}>
        <div className="flex items-center gap-2"><CalendarDays size={15} className="shrink-0" /> <strong>{majuscule(c.quand)}</strong></div>
        {c.description && <div className="text-[var(--muted)]">{c.description}</div>}
        <div className="flex items-start gap-2 text-[var(--muted)]"><Ticket size={15} className="shrink-0 mt-0.5" />
          {c.billet ? <span>Billet offert : {sansAnnee(c.billet)}</span> : <span>Sans billet</span>}</div>
      </div>

      {fait && (
        <div className="rounded-2xl px-4 py-3 text-sm mt-4 flex gap-2" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#15803D" }}
          data-testid="mon-benevolat-fait"><Check size={16} className="shrink-0 mt-0.5" /> {fait}</div>
      )}
      {erreur && (
        <div className="rounded-2xl px-4 py-3 text-sm mt-4 flex gap-2" style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C" }}
          data-testid="mon-benevolat-erreur"><AlertTriangle size={16} className="shrink-0 mt-0.5" /> {erreur}</div>
      )}

      {!annule && !c.modifiable && (
        <p className="text-sm mt-4 rounded-2xl px-4 py-3" style={{ background: "#FFFBEB", border: "1px solid #FDE68A", color: "#92400E" }}
          data-testid="mon-benevolat-trop-tard">
          Moins de 48 h avant la représentation : ça ne se change plus en ligne. Appelle l'école au <Telephone />.
        </p>
      )}

      {c.modifiable && !mode && (
        <>
          <div className="flex flex-wrap gap-3 mt-5">
            <button type="button" className="btn-primary" onClick={() => ouvrir("changer")} data-testid="mon-benevolat-changer">
              <CalendarDays size={16} /> Changer
            </button>
            <button type="button" className="btn-ghost" onClick={() => ouvrir("annuler")} data-testid="mon-benevolat-annuler">
              <X size={16} /> Annuler mon créneau
            </button>
          </div>
          <p className="text-xs text-[var(--muted)] mt-3">Possible en ligne jusqu'au {c.limite}.</p>
        </>
      )}

      {c.modifiable && mode === "changer" && (
        <div className="mt-5 space-y-5" data-testid="mon-benevolat-choix">
          <div>
            <div className="text-sm font-semibold mb-2">1. Quelle date ?</div>
            <div className="flex flex-wrap gap-2">
              {dates.map((r) => (
                <button key={r.id} type="button" onClick={() => { setRep(r.id); setRole(""); }} data-testid={`mon-benevolat-date-${r.id}`}
                  className="rounded-2xl px-3.5 py-2 text-sm text-left transition-colors"
                  style={{ border: `1.5px solid ${rep === r.id ? ROSE : "rgba(15,27,45,0.12)"}`, background: rep === r.id ? "#FFF7FB" : "#fff" }}>
                  <span className="font-semibold block">{majuscule(sansAnnee(r.quand))}</span>
                  <span className="text-xs text-[var(--muted)]">{r.titre}</span>
                </button>
              ))}
            </div>
          </div>
          {choisie && (
            <div>
              <div className="text-sm font-semibold mb-2">2. Quel rôle ?</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {choisie.roles.map((x) => {
                  const ici = actuel(x);
                  const possible = x.restantes > 0 && !ici;
                  return (
                    <button key={x.role} type="button" disabled={!possible} onClick={() => setRole(x.role)}
                      className="rounded-xl border bg-white px-3 py-2.5 text-left transition-colors disabled:opacity-50"
                      style={{ borderColor: role === x.role ? ROSE : "var(--border)", background: role === x.role ? "#FFF7FB" : "#fff" }}
                      data-testid={`mon-benevolat-role-${x.role}`}>
                      <span className="flex items-baseline justify-between gap-2">
                        <span className="font-medium">{EMOJI[x.role]} {x.libelle}</span>
                        <span className="text-xs shrink-0" style={{ color: ici ? ROSE : x.restantes ? "var(--muted)" : "#B91C1C" }}>
                          {ici ? "ton rôle actuel" : places(x.restantes)}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {pret && (
            <div className="rounded-2xl p-4 space-y-3" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }} data-testid="mon-benevolat-recap">
              <p className="text-sm">
                Ton créneau passe de <strong>{c.libelle} · {sansAnnee(c.quand)}</strong> à{" "}
                <strong>{choisie.roles.find((x) => x.role === role)?.libelle} · {sansAnnee(choisie.quand)}</strong>
                {choisie.titre !== c.titre ? ` (${choisie.titre})` : ""}.
                {c.billet && rep !== c.representation_id ? " Ton billet offert suit la nouvelle date quand c'est possible : l'e-mail te le dit." : ""}
              </p>
              <button type="button" className="btn-primary" disabled={envoi} onClick={() => agir("changer", { representation_id: rep, role })}
                data-testid="mon-benevolat-confirmer">{envoi ? "Un instant…" : "Confirmer le changement"}</button>
            </div>
          )}
          <button type="button" className="text-sm font-medium" style={{ color: "var(--primary)" }} onClick={() => setMode("")}
            data-testid="mon-benevolat-garder">Garder mon créneau</button>
        </div>
      )}

      {c.modifiable && mode === "annuler" && (
        <div className="mt-5" data-testid="mon-benevolat-annulation">
          <label className="text-sm text-[var(--muted)] block" htmlFor={`motif-${c.id}`}>
            Un mot pour l'école ? <span className="text-xs">(facultatif)</span>
          </label>
          <textarea id={`motif-${c.id}`} className="input-field mt-2" rows={2} maxLength={300} value={motif}
            onChange={(e) => setMotif(e.target.value)} data-testid="mon-benevolat-motif" />
          <div className="flex flex-wrap gap-3 mt-4">
            <button type="button" className="btn-primary" disabled={envoi} onClick={() => agir("annuler", { motif })}
              data-testid="mon-benevolat-confirmer-annulation" style={{ background: "#B91C1C" }}>
              {envoi ? "Un instant…" : "Confirmer l'annulation"}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setMode("")}>Garder mon créneau</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MonBenevolat() {
  useSEO({ title: "Mon bénévolat — 1000 ARTS", description: "Changer ou annuler ton créneau de bénévole aux spectacles de juin 2027.", canonical: "/mon-benevolat" });
  const [params] = useSearchParams();
  const jeton = params.get("j") || "";
  const [p, setP] = useState(null);
  const [erreur, setErreur] = useState("");

  useEffect(() => {
    if (!jeton) {
      setErreur("Ouvre cette page depuis le bouton « Changer ou annuler mon créneau » de ton e-mail de confirmation.");
      return;
    }
    fetch(`${API}/benevolat?j=${encodeURIComponent(jeton)}`).then(lire).then(setP).catch((e) => setErreur(e.message));
  }, [jeton]);

  // Les groupes WhatsApp des créneaux actifs (un par spectacle).
  const groupes = p ? [...new Map(p.creneaux.filter((c) => c.statut !== "annulee" && c.whatsapp).map((c) => [c.whatsapp, c.titre])).entries()] : [];

  return (
    <div className="pt-28 pb-24 relative overflow-hidden" data-testid="page-mon-benevolat">
      <PageDecor />
      <div className="max-w-2xl mx-auto px-6 relative">
        {/* Ouvert depuis l'app (app=1) : « ← Retour à l'app » (Jen, 01.11.2026). */}
        <RetourApp />
        <div className="eyebrow mb-3">Bénévoles · spectacles de juin 2027</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[0.95]">
          {p?.pour_autre ? <>Le bénévolat<br /><span style={{ color: ROSE }}>de {p.prenom}</span></> : <>Mon<br /><span style={{ color: ROSE }}>bénévolat</span></>}
        </h1>
        <p className="text-base text-[var(--muted)] mt-5 leading-relaxed">
          {p && !p.pour_autre ? `Merci ${p.prenom} ! ` : ""}Un empêchement ? Change de date ou de rôle, ou annule, jusqu'à 48 h avant
          la représentation. L'école est prévenue automatiquement.
        </p>

        {erreur && (
          <div className="rounded-2xl px-4 py-3 text-sm mt-8 flex gap-2" style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C" }}
            data-testid="mon-benevolat-erreur-page"><AlertTriangle size={16} className="shrink-0 mt-0.5" /> {erreur}</div>
        )}
        {!erreur && !p && <p className="text-[var(--muted)] mt-8">Chargement…</p>}
        {p && p.creneaux.map((c) => <Creneau key={c.id} c={c} p={p} jeton={jeton} onMaj={setP} />)}

        {groupes.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-6" data-testid="mon-benevolat-whatsapp">
            {groupes.map(([lien, titre]) => (
              <a key={lien} href={lien} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-white"
                style={{ background: "#25D366" }}>
                <MessageCircle size={16} /> Groupe WhatsApp{groupes.length > 1 ? ` · ${titre}` : " des bénévoles"}
              </a>
            ))}
          </div>
        )}

        <p className="text-sm text-[var(--muted)] mt-10 leading-relaxed flex items-start gap-2">
          <Phone size={15} className="shrink-0 mt-0.5" />
          <span>Une question ? Écris-nous à <a href="mailto:info1000arts@gmail.com" className="underline">info1000arts@gmail.com</a> ou
            appelle le <Telephone />.</span>
        </p>
        <Link to="/spectacles" className="btn-ghost mt-6 inline-flex">Les spectacles <ArrowRight size={16} /></Link>
      </div>
    </div>
  );
}
